import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateSubmission, useTargets } from '@/features/memorization/hooks';
import { ApiError } from '@/lib/api/client';

const TARGET_PICKER_LIMIT = 100;

/**
 * `POST /memorization/setoran` (`createSubmissionSchema`). Reuses the
 * Target context already fetched for this student (Phase G-1) rather than
 * duplicating a target lookup. Surah is derived from the selected target,
 * never independently entered, matching the backend's own requirement that
 * a submission's surah equal its target's surah - the ayah range is the
 * only choice this form actually exposes, bounded (client-side hint only)
 * to the target's own range; the backend remains the real validator.
 */
export function CreateSubmissionDialog({
  studentId,
  open,
  onOpenChange,
}: {
  readonly studentId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const [targetId, setTargetId] = useState('');
  const [startAyah, setStartAyah] = useState('');
  const [endAyah, setEndAyah] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const { data: targets, isLoading: targetsLoading } = useTargets(
    studentId,
    1,
    TARGET_PICKER_LIMIT,
  );
  const create = useCreateSubmission(studentId);

  const openTargets = targets?.items.filter((target) => target.status !== 'CANCELLED') ?? [];
  const selectedTarget = openTargets.find((target) => target.id === targetId);

  useEffect(() => {
    if (selectedTarget !== undefined) {
      setStartAyah(String(selectedTarget.startAyah));
      setEndAyah(String(selectedTarget.endAyah));
    }
    // Only re-derive defaults when the selected target itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId]);

  function reset(): void {
    setTargetId('');
    setStartAyah('');
    setEndAyah('');
    setValidationError(null);
    create.reset();
  }

  function handleOpenChange(next: boolean): void {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    if (selectedTarget === undefined) {
      setValidationError('Pilih target hafalan.');
      return;
    }
    const startAyahNumber = Number(startAyah);
    const endAyahNumber = Number(endAyah);
    if (startAyah === '' || !Number.isInteger(startAyahNumber) || startAyahNumber < 1) {
      setValidationError('Masukkan ayat awal yang valid.');
      return;
    }
    if (endAyah === '' || !Number.isInteger(endAyahNumber) || endAyahNumber < 1) {
      setValidationError('Masukkan ayat akhir yang valid.');
      return;
    }
    if (endAyahNumber < startAyahNumber) {
      setValidationError('Ayat akhir tidak boleh lebih kecil dari ayat awal.');
      return;
    }

    create.mutate(
      {
        targetId: selectedTarget.id,
        surah: selectedTarget.surah,
        startAyah: startAyahNumber,
        endAyah: endAyahNumber,
      },
      {
        onSuccess: () => {
          handleOpenChange(false);
        },
      },
    );
  }

  const apiErrorMessage =
    create.error instanceof ApiError
      ? create.error.message
      : create.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Catat Setoran</DialogTitle>
          <DialogDescription>
            Pilih target hafalan dan rentang ayat yang disetorkan.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="submission-target">Target Hafalan</Label>
            <Select value={targetId} onValueChange={setTargetId}>
              <SelectTrigger id="submission-target">
                <SelectValue placeholder={targetsLoading ? 'Memuat...' : 'Pilih target'} />
              </SelectTrigger>
              <SelectContent>
                {openTargets.map((target) => (
                  <SelectItem key={target.id} value={target.id}>
                    Surah {target.surah}: Ayat {target.startAyah}
                    {target.endAyah !== target.startAyah ? `-${target.endAyah}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!targetsLoading && openTargets.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Belum ada target hafalan aktif untuk santri ini.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="submission-start-ayah">Ayat Awal</Label>
              <Input
                id="submission-start-ayah"
                type="number"
                min={1}
                value={startAyah}
                onChange={(event) => {
                  setStartAyah(event.target.value);
                }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="submission-end-ayah">Ayat Akhir</Label>
              <Input
                id="submission-end-ayah"
                type="number"
                min={1}
                value={endAyah}
                onChange={(event) => {
                  setEndAyah(event.target.value);
                }}
              />
            </div>
          </div>

          {validationError !== null && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          {apiErrorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{apiErrorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
